import { ValidationError } from "class-validator";
import addDays from "date-fns/addDays";
import compareAsc from "date-fns/compareAsc";
import differenceInDays from "date-fns/differenceInDays";
import format from "date-fns/format";
import { City } from "../types";

export const TIME_SUFFIX = "T00:00:00"; // this is to deal with the date defaulting to utc, and displaying inccorect dates otherwise

export const getLocationLabel = (option: City | null) => {
  if (option) return option.name;
  return "";
};

export const getTravelDurationLabel = (durationMinutes: number) => {
  if (durationMinutes < 60) {
    return `${durationMinutes} ${durationMinutes > 1 ? "mins" : "min"}`;
  } else {
    const hours = Math.floor(durationMinutes / 60);
    const hoursUnit = hours > 1 ? "hrs" : "hr";
    const minutes = durationMinutes - hours * 60;
    const minutesUnit = minutes > 1 ? "mins" : "min";
    return `${
      minutes === 0
        ? `${hours} ${hoursUnit}`
        : `${hours} ${hoursUnit} ${minutes} ${minutesUnit}`
    }`;
  }
};

export const kgFormatter = new Intl.NumberFormat("en", {
  style: "unit",
  unit: "kilogram",
  maximumFractionDigits: 0,
});

export const formatCarbonAmount = (carbonInTonnes: number) => {
  return kgFormatter.format(carbonInTonnes * 1000);
};

export const getBestCarbonChoiceIndex = (
  emissions: Array<{ co2EmissionTonnes: number }>
): number => {
  return emissions.indexOf(
    emissions.reduce((a, b) =>
      a.co2EmissionTonnes <= b.co2EmissionTonnes ? a : b
    )
  );
};

export const formatToNumberWithCommas = (number: number) => {
  return new Intl.NumberFormat().format(number);
};

export const calculateOffsetDown = (optionCount: number) => {
  return optionCount * 2.5 + "rem + 2.5rem";
};

export const calculateOffsetUp = (optionCount: number) => {
  return -(optionCount * 2.5) + "rem - 2.5rem";
};

export const calculateOffsetDownReturnSelector = (optionCount: number) => {
  return optionCount * 2.5 + "rem + 6.1rem";
};

/**
 *
 * @param dateFromState e.g 2022-10-15
 * @returns true if date is less than 14 days or false
 */
export function isDateLessThanFourteenDaysFromToday(
  dateFromState: string
): boolean {
  const dateWithTime = new Date(`${dateFromState}${TIME_SUFFIX}`);

  const today = new Date();
  const dateInFourteenDays = addDays(today, 14);
  return (
    compareAsc(dateWithTime, dateInFourteenDays) !== 1 &&
    compareAsc(dateWithTime, dateInFourteenDays) !== 0
  );
}

/**
 *
 * @param departureDate e.g 2022-10-15
 * @param returnDate e.g 2022-10-17
 * @returns Parsed date
 */
export const getFormatedRangeDates = (
  departureDate: string,
  returnDate: string
): string => {
  const parsedReturnDate = new Date(`${returnDate}${TIME_SUFFIX}`);
  const parsedDepartureDate = new Date(`${departureDate}${TIME_SUFFIX}`);

  return parsedReturnDate.getFullYear() !== parsedDepartureDate.getFullYear()
    ? `${format(parsedDepartureDate, "dd MMM, yyyy")} - ${format(
        parsedReturnDate,
        "dd MMM, yyyy"
      )}`
    : `${format(parsedDepartureDate, "dd MMM")} - ${format(
        parsedReturnDate,
        "dd MMM, yyyy"
      )}`;
};

export const convertTravelDatesToString = (
  departureDate: string | undefined,
  returnDate: string | undefined
) => {
  if (!departureDate) return "-";
  if (!returnDate) {
    return format(new Date(`${departureDate}${TIME_SUFFIX}`), "dd MMM, yyyy");
  }
  return getFormatedRangeDates(departureDate, returnDate);
};

export const getValidationError = (
  property: string,
  errors: ValidationError[]
): string | undefined => {
  let errorMessage: string | undefined = undefined;

  for (const error of errors) {
    if (error.property === property) {
      // if the property has an error, there will be a constraints object
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const constraintKeys = Object.keys(error.constraints!);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const constraints = error.constraints!;

      if (constraintKeys.length === 1) {
        errorMessage = constraints[constraintKeys[0]];
      } else {
        const constraintKey = constraintKeys.find(
          key => key !== "maxLength" && key !== "minLength"
        );

        // multiple keys but none of them are max/min length
        if (constraintKey) {
          errorMessage = constraints[constraintKey];
        } else {
          // multiple keys, but they are max or min length
          errorMessage = constraints[constraintKeys[0]];
        }
      }
      break;
    } else if (error.children) {
      errorMessage = getValidationError(property, error.children);
    }
  }

  return errorMessage;
};

export const getNumberOfNights = (
  fromDateFromState: string,
  toDateFromState: string
) => {
  const fromDateWithTime = new Date(`${fromDateFromState}${TIME_SUFFIX}`);
  const toDateWithTime = new Date(`${toDateFromState}${TIME_SUFFIX}`);

  return differenceInDays(toDateWithTime, fromDateWithTime);
};

export const getTripTotalCarbon = (
  selectedTransportOptionsTotalCarbon: number,
  hotelCarbonInTonnes: number | undefined
) => {
  const totalCarbon = hotelCarbonInTonnes
    ? selectedTransportOptionsTotalCarbon + hotelCarbonInTonnes
    : selectedTransportOptionsTotalCarbon;

  return totalCarbon;
};
